import {
  Typography,
  Avatar,
  Button,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
} from "@mui/material";
import React from "react";
import { DataGrid } from "@mui/x-data-grid";
import API from "../API";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import { DateRangePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs from "dayjs";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";

const VEHICLE_NAMES = {
  0: "Motor",
  2: "Doblo",
  4: "Panelvan",
  5: "Kamyonet",
  7: "Kamyon (6 Teker)",
  8: "Kamyon (8 Teker)",
  9: "Kamyon (10 Teker)",
};

const cityOptions = Object.values(API.allowedCities || {})
  .map((city) => ({
    label: city.name,
    value: city.id,
  }))
  .sort((a, b) => a.label.localeCompare(b.label, "tr"));

const vehicleOptions = Object.entries(VEHICLE_NAMES).map(([value, label]) => ({
  label,
  value: Number(value),
}));

const filterLabelStyle = {
  fontSize: 11,
  color: "#666",
  margin: "0 0 4px",
};

const filterSelectSx = {
  "& .MuiSelect-select": {
    fontSize: 12,
    color: "#1F2937",
    paddingTop: "8px",
    paddingBottom: "8px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ddd",
    borderRadius: "6px",
  },
};

const shortcutsItems = [
  {
    label: "Bugün",
    getValue: () => {
      const today = dayjs();
      return [today.startOf("day"), today.endOf("day")];
    },
  },
  {
    label: "Bu Hafta",
    getValue: () => {
      const today = dayjs();
      return [today.startOf("week"), today.endOf("week")];
    },
  },
  {
    label: "Geçen Hafta",
    getValue: () => {
      const today = dayjs();
      const prevWeek = today.subtract(7, "day");
      return [prevWeek.startOf("week"), prevWeek.endOf("week")];
    },
  },
  {
    label: "Son 7 Gün",
    getValue: () => {
      const today = dayjs();
      return [today.subtract(7, "day"), today];
    },
  },
  {
    label: "Bu Ay",
    getValue: () => {
      const today = dayjs();
      return [today.startOf("month"), today.endOf("month")];
    },
  },
  { label: "Her zaman", getValue: () => [null, null] },
];

export default class DriversScreen extends React.Component {
  constructor() {
    super();

    this.state = {
      drivers: [],
      cityFilter: [],
      vehicleFilter: [],
      ownerFilter: "",
      statusFilter: "",
      ownerStats: {},
      driverCount: 0,
      paginationModel: { page: 0, pageSize: 10 },
    };

    this.approvalDateFilter = null;
  }

  componentDidMount() {
    this.getDriverList();
  }

  getDriverList = (page = this.state.paginationModel.page) => {
    API.fetch("getdrivers", {
      page,
      cityFilter: this.state.cityFilter,
      vehicleFilter: this.state.vehicleFilter,
      ownerFilter: this.state.ownerFilter,
      approvalDateFilter: this.approvalDateFilter,
      verifiedOnly: this.state.statusFilter === "pending",
      validOnly: this.state.statusFilter === "approved",
    }).then((response) => {
      this.setState({
        drivers: response.drivers || [],
        driverCount: response.driverCount || 0,
        ownerStats: response.ownerStats || {},
        paginationModel: {
          ...this.state.paginationModel,
          page,
        },
      });
    });
  };

  renderSelectedChips = (selectedValues, options) => {
    if (!selectedValues.length) {
      return <span style={{ color: "#6B7280" }}>Tümü</span>;
    }

    return selectedValues
      .map((value) => options.find((item) => item.value === value)?.label || value)
      .join(", ");
  };

  renderOwnerSummary() {
    if (!this.state.ownerFilter) return null;

    const stats = this.state.ownerStats[this.state.ownerFilter];
    if (!stats) return null;

    return (
      <div
        style={{
          marginTop: 16,
          marginBottom: 8,
          padding: "10px 14px",
          borderRadius: 8,
          backgroundColor: "#EEF2FF",
          color: "#3730A3",
          fontWeight: 600,
        }}
      >
        {`${stats.name} • ${stats.approved || 0} sürücü onayı`}
      </div>
    );
  }

  render() {
    const ownerOptions = Object.entries(this.state.ownerStats)
      .map(([id, stats]) => ({ id, ...stats }))
      .filter((owner) => owner.name && owner.name.trim().length > 0)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));

    return (
      <div>
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 16,
            alignItems: "flex-end",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div style={{ minWidth: 220 }}>
              <p style={filterLabelStyle}>Onay Tarihi</p>
              <DateRangePicker
                onAccept={(range) => {
                  if (
                    !(
                      (range[0] == null && range[1] == null) ||
                      (range[0] != null && range[1] != null)
                    )
                  ) {
                    return;
                  }

                  if (range[0] != null) {
                    const start = new Date(range[0].$d);
                    start.setHours(0, 0, 0, 0);

                    const end = new Date(range[1].$d);
                    end.setHours(23, 59, 59, 999);

                    this.approvalDateFilter = {
                      gte: start.getTime(),
                      lte: end.getTime(),
                    };
                  } else {
                    this.approvalDateFilter = null;
                  }

                  this.setState(
                    {
                      paginationModel: { ...this.state.paginationModel, page: 0 },
                    },
                    () => this.getDriverList(0),
                  );
                }}
                slotProps={{
                  shortcuts: { items: shortcutsItems },
                  actionBar: { actions: [] },
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "AA/GG/YYYY - AA/GG/YYYY",
                    sx: {
                      "& .MuiInputBase-input": {
                        fontSize: 12,
                        paddingTop: "8px",
                        paddingBottom: "8px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#ddd",
                        borderRadius: "6px",
                      },
                    },
                  },
                }}
                slots={{ field: SingleInputDateRangeField }}
              />
            </div>
          </LocalizationProvider>

          <div style={{ minWidth: 180 }}>
            <p style={filterLabelStyle}>Şehir</p>
            <FormControl size="small" fullWidth>
              <Select
                multiple
                displayEmpty
                value={this.state.cityFilter}
                onChange={(event) => {
                  this.setState(
                    {
                      cityFilter: event.target.value,
                      paginationModel: { ...this.state.paginationModel, page: 0 },
                    },
                    () => this.getDriverList(0),
                  );
                }}
                input={<OutlinedInput />}
                renderValue={(selected) =>
                  this.renderSelectedChips(selected, cityOptions)
                }
                sx={filterSelectSx}
              >
                {cityOptions.map((city) => (
                  <MenuItem key={city.value} value={city.value}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <span style={{ flexGrow: 1 }}>{city.label}</span>
                      {this.state.cityFilter.includes(city.value) && (
                        <CheckIcon sx={{ fontSize: 18, color: "#4F46E5" }} />
                      )}
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div style={{ minWidth: 190 }}>
            <p style={filterLabelStyle}>Araç Tipi</p>
            <FormControl size="small" fullWidth>
              <Select
                multiple
                displayEmpty
                value={this.state.vehicleFilter}
                onChange={(event) => {
                  this.setState(
                    {
                      vehicleFilter: event.target.value,
                      paginationModel: { ...this.state.paginationModel, page: 0 },
                    },
                    () => this.getDriverList(0),
                  );
                }}
                input={<OutlinedInput />}
                renderValue={(selected) =>
                  this.renderSelectedChips(selected, vehicleOptions)
                }
                sx={filterSelectSx}
              >
                {vehicleOptions.map((vehicle) => (
                  <MenuItem key={vehicle.value} value={vehicle.value}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <span style={{ flexGrow: 1 }}>{vehicle.label}</span>
                      {this.state.vehicleFilter.includes(vehicle.value) && (
                        <CheckIcon sx={{ fontSize: 18, color: "#4F46E5" }} />
                      )}
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div style={{ minWidth: 170 }}>
            <p style={filterLabelStyle}>Fatura Onay Durumu</p>
            <FormControl size="small" fullWidth>
              <Select
                displayEmpty
                value={this.state.statusFilter}
                onChange={(event) => {
                  this.setState(
                    {
                      statusFilter: event.target.value,
                      paginationModel: { ...this.state.paginationModel, page: 0 },
                    },
                    () => this.getDriverList(0),
                  );
                }}
                sx={filterSelectSx}
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="pending">Fatura Onayı Bekliyor</MenuItem>
                <MenuItem value="approved">Fatura Onaylanmış</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div style={{ minWidth: 190 }}>
            <p style={filterLabelStyle}>Sorumlu / Onaylayan</p>
            <FormControl size="small" fullWidth>
              <Select
                displayEmpty
                value={this.state.ownerFilter}
                onChange={(event) => {
                  this.setState(
                    {
                      ownerFilter: event.target.value,
                      paginationModel: { ...this.state.paginationModel, page: 0 },
                    },
                    () => this.getDriverList(0),
                  );
                }}
                sx={filterSelectSx}
              >
                <MenuItem value="">Tümü</MenuItem>
                {ownerOptions.map((owner) => (
                  <MenuItem key={owner.id} value={owner.id}>
                    {owner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        <Badge style={{ marginTop: 32, padding: 0 }}>
          <div style={{ padding: 24 }}>
            <Typography variant="h5">Sürücü Listesi</Typography>
            {this.renderOwnerSummary()}
          </div>
          <DataGrid
            autoHeight
            rows={this.state.drivers.map((driver) => ({ ...driver, id: driver._id }))}
            columns={[
              {
                field: "name",
                headerName: "Ad Soyad",
                flex: 1,
                renderCell: (params) => (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      src={`https://ik.imagekit.io/tasimacim/driver/tr:w-80/${params.row._id}.jpeg`}
                    />
                    <div style={{ marginLeft: 10 }}>
                      <Typography variant="body1">{params.row.name}</Typography>
                      <Typography variant="body2">
                        {params.row.email}
                      </Typography>
                    </div>
                  </div>
                ),
              },
              {
                field: "vehicles",
                headerName: "Araç",
                flex: 1,
                valueGetter: (params, row) =>
                  (row.vehicles || [])
                    .map((vehicle) => VEHICLE_NAMES[vehicle.type])
                    .filter(Boolean)
                    .join(", "),
              },
              {
                field: "city",
                headerName: "Şehir",
                flex: 0.8,
                valueGetter: (params, row) => API.getCityName(row.city),
              },
              {
                field: "phone",
                headerName: "Telefon",
                flex: 1,
              },
              {
                field: "approverWorkerName",
                headerName: "Sorumlu / Onaylayan",
                flex: 1,
                valueGetter: (params, row) => row.approverWorkerName || "-",
              },
              {
                field: "approvedAt",
                headerName: "Onay Tarihi",
                flex: 1,
                valueGetter: (params, row) => {
                  const approvedAt = row.approvedAt || getDateFromObjectId(row._id);
                  return approvedAt
                    ? new Date(approvedAt).toLocaleString("tr-TR")
                    : "-";
                },
              },
              {
                field: "open",
                headerName: "",
                sortable: false,
                width: 70,
                renderCell: (params) => (
                  <a href={`../driver/${params.row._id}`}>
                    <Button>
                      <ArrowForwardIcon />
                    </Button>
                  </a>
                ),
              },
            ]}
            paginationModel={this.state.paginationModel}
            onPaginationModelChange={(model) => {
              this.setState({ paginationModel: model });
              this.getDriverList(model.page);
            }}
            pageSizeOptions={[10, 25, 50]}
            slotProps={{
              pagination: { showFirstButton: true, showLastButton: true },
            }}
            rowCount={this.state.driverCount}
            paginationMode="server"
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F3F4F6",
                borderRadius: 0,
              },
            }}
          />
        </Badge>
      </div>
    );
  }
}

const Badge = (props) => {
  return (
    <div
      style={{
        backgroundColor: "white",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        boxShadow:
          "rgb(100 116 139 / 6%) 0px 1px 1px, rgb(100 116 139 / 10%) 0px 1px 2px",
        borderRadius: 8,
        padding: 24,
        color: "rgb(18, 24, 40)",
        ...props.style,
      }}
    >
      {props.children}
    </div>
  );
};

function getDateFromObjectId(id) {
  if (!id || typeof id !== "string" || id.length < 8) return null;
  return parseInt(id.substring(0, 8), 16) * 1000;
}
